import '../ui/museum.css';
import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';
import {
  ITEMS, KINDS, RARITY_VALUE,
  hasFound, foundCount, collectionStats,
  addFound, randomFind          // debug-only: "donate a sample" button
} from '../core/collection.js';
import { getFlag, setFlag } from '../core/save.js';
import { flagObjective, isQuestStarted } from '../core/quests.js';


// The Museum — where Bread Boi shows off everything dug up around Toast Town.
// A marble hall with three wings (Paleo Hall / Antiquities / Lost & Found), one
// display case per collectible: filled cases show a rarity gem + the item icon
// and nameplate, empty ones show a "?" silhouette under glass. A left-side
// placard tracks overall progress + a "town prestige" score derived from what's
// on display. Read-only this round (no crumb payouts yet) — a wholesome trophy
// room that fills in as the dig-spots start dropping finds.

export function createMuseumScene(){
  return () => new MuseumScene();
}

// marble-ish hall palette
const MARBLE   = 0xEDE7DA;
const MARBLE_D = 0xCFC6B4;
const WALL     = 0xE3D9C6;
const WALL_D   = 0xCBBFA6;
const TRIM     = 0x8C6239;

// gem tint by rarity (also drives the prestige value)
const RARITY_COLOR = { common: 0xCFD6DB, uncommon: 0x7FBF6A, rare: 0x3D5FC0, legendary: 0xF4B233 };

// One wing per KIND. `cols` × `rows` must give AT LEAST as many pedestals as that
// kind has items in collection.js (fossil 3×2=6 for 6, artifact 3×4=12 for 11,
// junk 2×1=2 for 2) — an item with no slot gets no case, and if a quest asks the
// player to inspect it, that quest is unwinnable. `npm run test:play` audits this.
// `rug` tints the floor accent so the wings read as distinct bays; `rugSize`/`rugZ`
// override its default footprint for a wing whose grid has outgrown it.
const WINGS = {
  fossil: {
    title: 'Paleo Hall', icon: '🦴', cx: -8, rug: 0x8A6E4E,
    cols: [-10.6, -8, -5.4], rows: [-6.8, -3.4],
    hint: 'No fossils here yet — dig at the sparkly spots in the Forest to unearth them!'
  },
  artifact: {
    title: 'Antiquities', icon: '🏺', cx: 0, rug: 0x5F7A72,
    cols: [-3.0, 0, 3.0], rows: [-7.2, -4.6, -2.0, 0.6],
    rugSize: [6.4, 9.0], rugZ: -3.7,
    hint: 'This case is empty — old artifacts turn up when you dig around Toast Town.'
  },
  junk: {
    title: 'Lost & Found', icon: '🔎', cx: 8, rug: 0x556A82,
    cols: [6.6, 9.4], rows: [-5.1],
    hint: 'One toast’s trash… keep digging and even junk earns a spot on display!'
  }
};

// Only iterate kinds that actually have a museum wing (fish live in the Aquarium).
const MUSEUM_KINDS = KINDS.filter(k => WINGS[k]);

class MuseumScene {
  constructor(){
    this.panels = [];             // modal DOM (curator's desk) — cleared on close/exit
    this.gems = [];               // rotating rarity gems, animated in update()
    this.progressEl = null;       // persistent left-side placard
    this.loreSpots = [];          // museum lore plaques for the Baker King quest

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xF2ECE0);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 12;
    this.camTarget = new THREE.Vector3(0, 1.6, 2);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildHall();

    this.exhibitGroup = new THREE.Group();
    this.scene.add(this.exhibitGroup);
    this._buildExhibits();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 7.5);
    this.bread.root.rotation.y = Math.PI;   // facing into the hall
    this.scene.add(this.bread.root);

    this.nearSpot = null;
  }

  // ---- static hall ----
  _buildLights(){
    // bright, airy skylight feel
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.72));
    this.scene.add(new THREE.HemisphereLight(0xFFFDF5, 0xCFC6B4, 0.55));
    const sky = new THREE.DirectionalLight(0xFFF8E8, 0.7);
    sky.position.set(2, 18, 6); sky.castShadow = true;
    sky.shadow.mapSize.set(2048, 2048);
    const s = 20;
    sky.shadow.camera.left = -s; sky.shadow.camera.right = s;
    sky.shadow.camera.top = s; sky.shadow.camera.bottom = -s;
    this.scene.add(sky);
  }

  _buildHall(){
    // marble floor with a checker of lighter/darker tiles
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(28, 22), new THREE.MeshLambertMaterial({ color: MARBLE }));
    floor.rotation.x = -Math.PI/2; floor.position.set(0, 0, -1); floor.receiveShadow = true;
    this.scene.add(floor);
    for (let gx = -12; gx <= 12; gx += 4)
      for (let gz = -9; gz <= 7; gz += 4){
        if (((gx + gz) / 4) % 2) continue;
        const tile = new THREE.Mesh(new THREE.PlaneGeometry(3.9, 3.9), new THREE.MeshLambertMaterial({ color: MARBLE_D }));
        tile.rotation.x = -Math.PI/2; tile.position.set(gx, 0.005, gz); tile.receiveShadow = true;
        this.scene.add(tile);
      }
    // a plush runner down the central aisle
    const runner = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 18), new THREE.MeshLambertMaterial({ color: C.raspberry }));
    runner.rotation.x = -Math.PI/2; runner.position.set(0, 0.01, 0); this.scene.add(runner);

    // back wall + side walls
    const back = new THREE.Mesh(new THREE.PlaneGeometry(28, 9), new THREE.MeshLambertMaterial({ color: WALL }));
    back.position.set(0, 4.5, -10); back.receiveShadow = true; this.scene.add(back);
    box(28, 0.5, 0.5, TRIM, 0, 3.4, -9.9, this.scene);          // wainscot trim
    const sideL = new THREE.Mesh(new THREE.PlaneGeometry(22, 9), new THREE.MeshLambertMaterial({ color: WALL_D }));
    sideL.rotation.y = Math.PI/2; sideL.position.set(-13, 4.5, -1); this.scene.add(sideL);
    const sideR = sideL.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(13, 4.5, -1); this.scene.add(sideR);

    // skylight ceiling: pale glowing panels in a grid up high
    const glass = new THREE.MeshBasicMaterial({ color: 0xFBF6E6 });
    for (let x = -9; x <= 9; x += 4.5)
      for (let z = -8; z <= 4; z += 4){
        const panel = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 3.0), glass);
        panel.rotation.x = Math.PI/2; panel.position.set(x, 8.2, z); this.scene.add(panel);
      }

    // per-wing rugs + banners
    for (const k of MUSEUM_KINDS){
      const w = WINGS[k];
      const [rugW, rugD] = w.rugSize || [6.4, 6.2];
      const rug = new THREE.Mesh(new THREE.PlaneGeometry(rugW, rugD), new THREE.MeshLambertMaterial({ color: w.rug }));
      rug.rotation.x = -Math.PI/2; rug.position.set(w.cx, 0.015, w.rugZ ?? -5.2); this.scene.add(rug);
      const banner = textSprite(`${w.icon} ${w.title}`, 26, '#3A2A1C', 4.6, 1.05);
      banner.position.set(w.cx, 5.6, -9.7); this.scene.add(banner);
    }

    // lore plaques along the entrance wall
    this._buildLorePlaques();

    // grand entrance sign + the exit archway home to downtown
    const marquee = textSprite('THE MUSEUM', 30, '#6B4A2F', 5.2, 1.15);
    marquee.position.set(0, 7.1, -9.7); this.scene.add(marquee);
    cyl(0.18, 0.18, 3.4, TRIM, -2.2, 1.7, 8.6, this.scene, 10);
    cyl(0.18, 0.18, 3.4, TRIM,  2.2, 1.7, 8.6, this.scene, 10);
    box(5.2, 0.45, 0.4, TRIM, 0, 3.5, 8.6, this.scene);
    const exitSign = textSprite('← to downtown', 24, '#6B4A2F', 3.6, 0.85);
    exitSign.position.set(0, 4.1, 8.6); this.scene.add(exitSign);

    // curator's front desk (opens the info panel)
    const desk = new THREE.Group(); desk.position.set(-4.2, 0, 5.6); this.scene.add(desk);
    box(2.4, 1.1, 0.9, C.rye, 0, 0.55, 0, desk);
    box(2.6, 0.14, 1.05, C.crustDark, 0, 1.16, 0, desk);
    const plaque = textSprite('📋 Curator', 22, '#3A2A1C', 2.0, 0.7);
    plaque.position.set(-4.2, 1.9, 5.6); this.scene.add(plaque);
    this.deskPos = new THREE.Vector3(-4.2, 0, 5.6);
  }

  // ---- exhibits (rebuilt whenever the collection changes) ----
  _buildExhibits(){
    // clear any previous pedestals + gems
    for (let i = this.exhibitGroup.children.length - 1; i >= 0; i--) this.exhibitGroup.remove(this.exhibitGroup.children[i]);
    this.gems = [];
    this.caseSpots = [];        // { pos, r, filled, item } for proximity walk-ups

    for (const k of KINDS){
      const w = WINGS[k];
      if (!w) continue; // fish live in the Aquarium, not the Museum
      const ids = Object.keys(ITEMS).filter(id => ITEMS[id].kind === k);
      // slot order: row by row, left to right
      const slots = [];
      for (const z of w.rows) for (const x of w.cols) slots.push({ x, z });
      ids.forEach((id, i) => {
        const slot = slots[i];
        if (!slot){             // more items than slots: this item gets NO case at all
          console.warn(`museum: '${id}' has no pedestal — widen the ${k} wing's cols/rows.`);
          return;
        }
        this._pedestal(id, w, slot.x, slot.z);
      });
    }
  }

  _pedestal(id, wing, x, z){
    const item = ITEMS[id];
    const filled = hasFound(id);
    const g = new THREE.Group(); g.position.set(x, 0, z); this.exhibitGroup.add(g);

    // marble column
    cyl(0.48, 0.54, 0.22, MARBLE_D, 0, 0.11, 0, g, 20);   // base
    cyl(0.32, 0.36, 0.98, MARBLE,   0, 0.71, 0, g, 20);   // shaft
    cyl(0.44, 0.42, 0.14, MARBLE_D, 0, 1.27, 0, g, 20);   // cap

    if (filled){
      // rarity gem: two cones tip-to-tip, slowly spinning
      const gem = new THREE.Group(); gem.position.set(0, 1.75, 0);
      const col = RARITY_COLOR[item.rarity] || 0xCFD6DB;
      cyl(0, 0.2, 0.28, col, 0, 0.14, 0, gem, 6);         // upper facet
      cyl(0.2, 0, 0.18, col, 0, -0.09, 0, gem, 6);        // lower point
      g.add(gem);
      this.gems.push(gem);
      // item icon floating above + nameplate on the front face
      const ic = textSprite(item.icon, 44, '#000000', 0.9, 0.9);
      ic.position.set(0, 2.5, 0); g.add(ic);
      const plate = textSprite(item.name, 19, '#3A2A1C', 2.2, 0.6);
      plate.position.set(0, 0.62, 0.5); g.add(plate);
    } else {
      // empty case: faint glass box + a "?" silhouette
      const glass = new THREE.Mesh(
        new THREE.BoxGeometry(0.86, 1.0, 0.86),
        new THREE.MeshLambertMaterial({ color: 0xDCE6EA, transparent: true, opacity: 0.22 })
      );
      glass.position.set(0, 1.85, 0); g.add(glass);
      const q = textSprite('?', 52, '#9C907C', 0.8, 0.8);
      q.position.set(0, 1.85, 0); g.add(q);
      const plate = textSprite('? ? ?', 19, '#9C907C', 2.0, 0.6);
      plate.position.set(0, 0.62, 0.5); g.add(plate);
    }

    this.caseSpots.push({ pos: new THREE.Vector3(x, 0, z + 1.1), r: 1.7, filled, id, wing });
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.66];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the museum');
    HUD.say('The Museum! Every fossil, artifact and bit of junk you dig up gets a display case here. Walk the wings to see what you’ve found — and what’s still out there.');
    HUD.setHint('click to walk · walk up to a case, the curator’s desk, or the exit');
    HUD.hideHintSoon(8000);
    this._buildProgress();
  }

  exit(){
    this._clearPanels();
    if (this.progressEl){ this.progressEl.remove(); this.progressEl = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _tap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-12, Math.min(12, gp.x));
    gp.z = Math.max(-8.2, Math.min(8.4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  // ---- proximity messaging ----
  _buildLorePlaques(){
    const LORE = [
      { x: -9, z: 7, title: 'The First Oven', text: 'In the time before toasting, the Baker King pulled the first loaf from the Primordial Oven. Its crust became the hills, its crumbs the stars.' },
      { x: -4.5, z: 7, title: 'The Sealed Fortress', text: 'The King hid his Golden Loaf behind three symbols: Crust, Toast, and Jam. Only the worthy may warm the stone.' },
      { x: 0, z: 7, title: 'The Royal Regalia', text: 'Crown, Scepter, Gauntlet, and Bread-Bone — four relics scattered among the townsfolk. Gather them to crown the crumb.' }
    ];
    LORE.forEach((l, i) => {
      const g = new THREE.Group(); g.position.set(l.x, 0, l.z); this.scene.add(g);
      box(1.6, 1.0, 0.12, 0xD4C8B8, 0, 0.5, 0, g);
      box(1.5, 0.9, 0.04, 0xF7F0E4, 0, 0.5, 0.06, g);
      const label = textSprite(`📜 ${l.title}`, 20, '#4A3A2A', 2.2, 0.7);
      label.position.set(l.x, 1.3, l.z); this.scene.add(label);
      this.loreSpots.push({ pos: new THREE.Vector3(l.x, 0, l.z + 0.9), r: 1.6, id: i, title: l.title, text: l.text });
    });
  }

  _readPlaque(s){
    const key = `museumLore${s.id}`;
    if (!getFlag(key)){
      setFlag(key, true);
      const allRead = [0, 1, 2].every(i => getFlag(`museumLore${i}`));
      if (allRead) flagObjective('crust-archives', 'readMuseumLore');
    }
    this.bread.drawFace('grin');
    HUD.say(`${s.title}: ${s.text}`);
  }

  _hotspots(){
    return [
      { pos: this.deskPos, r: 1.9, label: '📋 visit the curator’s desk', action: () => this._openDesk() },
      ...this.loreSpots.map(s => ({
        pos: s.pos, r: s.r,
        label: getFlag(`museumLore${s.id}`) ? `📜 ${s.title}` : '📜 read the plaque',
        action: () => this._readPlaque(s)
      })),
      ...this.caseSpots.map(s => ({
        pos: s.pos, r: s.r,
        label: s.filled ? '🔍 inspect the exhibit' : '🔍 peek in the empty case',
        action: () => this._inspectCase(s)
      })),
      { pos: new THREE.Vector3(0, 0, 8.4), r: 2.2, label: '← back to downtown', action: () => this.manager.goTo('downtown', { from: 'museum' }) }
    ];
  }

  _inspectCase(s){
    const item = ITEMS[s.id];
    if (s.filled){
      const n = foundCount(s.id);
      const extra = n > 1 ? ` · ${n} on file` : '';
      HUD.say(`${item.icon} ${item.name} — ${item.rarity} ${item.kind}${extra}.`);
      this.bread.drawFace('yum');
      if (s.id === 'relic-quadra-valve') flagObjective('intern-gary', 'viewedInternBadge');
    } else {
      HUD.say(WINGS[item.kind].hint);
      this.bread.drawFace('o');
    }
  }

  // ---- curator's desk info modal (own DOM + museum.css) ----
  _panel(html){
    const el = document.createElement('div');
    el.className = 'mus-modal';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _openDesk(){
    this._clearPanels();
    this.nearSpot = null;
    this.input.onTap = null;              // pause walking while the desk is open
    HUD.showEnter(null);
    this.bread.drawFace('grin');
    const st = collectionStats();
    const prestige = this._prestige();
    const kindRows = MUSEUM_KINDS.map(k => {
      const b = st.byKind[k];
      return `<div><b>${b.found}/${b.total}</b><span>${WINGS[k].title}</span></div>`;
    }).join('');

    const p = this._panel(`
      <div class="mus-card">
        <h2>Curator’s Desk</h2>
        <div class="mus-sub">"Welcome! Every find earns a place in the hall."</div>
        <div class="mus-stats">${kindRows}</div>
        <div class="mus-prestige-big">exhibits on display: <b>${st.found}/${st.total}</b></div>
        <div class="mus-prestige-big">town prestige: <b>${prestige}</b></div>
        <button class="mus-btn ghost close">← back to the hall</button>
        <button class="mus-btn debug donate">🧪 debug: donate a random sample</button>
        <div class="mus-debug-note">Debug tool for testing the Museum before dig-spots drop real finds. It fabricates a random collectible into your save.</div>
      </div>`);
    p.querySelector('.close').onclick = () => this._closeDesk();
    p.querySelector('.donate').onclick = () => {
      addFound(randomFind());
      this._buildExhibits();            // a case may have just filled in
      this._updateProgress();
      this._openDesk();                 // re-render the desk with fresh numbers
    };
  }

  _closeDesk(){
    this._clearPanels();
    this.input.onTap = (ndc, ray) => this._tap(ray);
  }

  // ---- persistent progress placard ----
  _prestige(){
    // "town prestige": summed rarity value of everything currently on display
    let total = 0;
    for (const id in ITEMS) if (hasFound(id)) total += (RARITY_VALUE[ITEMS[id].rarity] || 0);
    return total;
  }

  _buildProgress(){
    if (this.progressEl) this.progressEl.remove();
    const el = document.createElement('div');
    el.className = 'mus-progress';
    document.body.appendChild(el);
    this.progressEl = el;
    this._updateProgress();
  }

  _updateProgress(){
    if (!this.progressEl) return;
    const st = collectionStats();
    const kindRows = MUSEUM_KINDS.map(k => {
      const b = st.byKind[k];
      const pct = b.total ? Math.round((b.found / b.total) * 100) : 0;
      return `<div class="mus-krow">
        <div class="mus-klabel"><span>${WINGS[k].title}</span><b>${b.found}/${b.total}</b></div>
        <div class="mus-bar"><span style="width:${pct}%"></span></div>
      </div>`;
    }).join('');
    this.progressEl.innerHTML = `
      <h3>Collection</h3>
      <div class="mus-prestige"><b>${this._prestige()}</b><span>town prestige</span></div>
      <div class="mus-total"><b>${st.found}</b> / ${st.total} exhibits on display</div>
      ${kindRows}`;
  }

  update(dt){
    this.bread.update(dt);

    // slowly spin the rarity gems for a little sparkle
    for (const gem of this.gems){
      gem.rotation.y += dt * 0.9;
      gem.position.y = 1.75 + Math.sin(performance.now() * 0.002 + gem.rotation.y) * 0.05;
    }

    // proximity prompts (skip while the desk modal is open)
    if (!this.panels.length){
      const spots = this._hotspots();
      let near = null, nd = Infinity;
      spots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        const key = near.label || near.id;
        if (this.nearSpot !== key){
          this.nearSpot = key;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
